import React, { useState } from 'react';
import { X } from 'lucide-react';
import { createChannel } from '../utils/chatHelpers';

const CreateChannelModal = ({ onClose, onChannelCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Channel name is required');
      return;
    }

    // Validate channel name format
    if (!/^[a-z0-9-_]+$/.test(name)) {
      setError('Channel name can only contain lowercase letters, numbers, hyphens, and underscores');
      return;
    }

    setCreating(true);

    const { data, error: createError } = await createChannel({
      name: name.toLowerCase().trim(),
      description: description.trim(),
      is_private: isPrivate
    });

    if (createError) {
      setError(createError.message || 'Failed to create channel');
      setCreating(false);
      return;
    }

    if (data) {
      onChannelCreated(data);
    }

    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-black max-w-md w-full p-8 rounded-2xl border border-white/20 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Create Channel</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Channel Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Channel Name
            </label>
            <div className="flex items-center">
              <span className="text-gray-400 mr-2">#</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="general"
                className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-white transition-colors"
                disabled={creating}
                required
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Use lowercase letters, numbers, hyphens, and underscores only
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this channel about?"
              rows={3}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-white transition-colors resize-none"
              disabled={creating}
            />
          </div>

          {/* Privacy Setting */}
          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
            <input
              type="checkbox"
              id="isPrivate"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              className="w-4 h-4 rounded"
              disabled={creating}
            />
            <div className="flex-1">
              <label htmlFor="isPrivate" className="text-sm font-medium text-white cursor-pointer">
                Make private
              </label>
              <p className="text-xs text-gray-400">
                Only invited members can access this channel
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-950/20 border border-red-900 rounded-lg">
              <p className="text-sm text-red-500 font-medium">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white/5 border border-white/10 hover:bg-white/10 rounded-lg text-white font-medium transition-colors"
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-white hover:bg-gray-200 disabled:bg-gray-800 disabled:cursor-not-allowed rounded-lg text-black font-bold transition-all shadow-[0_0_15px_rgba(255,255,255,0.1)]"
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create Channel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateChannelModal;
